/**
 * Build-time feature flag for the new design (v2) — opt-in.
 *
 * - The new design exists only in a build made with REACT_APP_ENABLE_V2=true (exactly "true").
 *   Unset, "false" or any other value gives a classic-only build: no design toggle anywhere,
 *   `?design=v2` is ignored and not stored, saved "v2" preferences (device default, per-doctor
 *   record, the display key) are ignored, and AppRouter never renders V2App.
 * - CRA inlines `process.env.REACT_APP_ENABLE_V2` at build time. AppRouter guards the lazy
 *   `import("../../v2/V2App")` with the same literal comparison, so when the variable is defined
 *   and not "true" webpack drops that import and emits no V2App chunk (the Dockerfile defines it,
 *   default "false").
 * - Saved "v2" preferences are left in storage untouched, so a later v2-enabled build brings back
 *   each doctor's own choice.
 *
 * Plain module (no React, no router) so the decisions can be unit-tested in Jest.
 */
import { KeyValueStorage, migrateUiPrefs, readDisplayPrefs, recordUiChoice } from "../../shared/storage/uiPreferences";

export type Design = "legacy" | "v2";

/** True only in a build made with REACT_APP_ENABLE_V2=true. Read at call time (tests flip it). */
export const isV2Enabled = (): boolean => process.env.REACT_APP_ENABLE_V2 === "true";

/** The design a stored or requested value gives in this build: "v2" only when v2 is enabled. */
export function allowedDesign(value: unknown): Design {
  return isV2Enabled() && value === "v2" ? "v2" : "legacy";
}

/**
 * The design shown at start-up. With v2 disabled: always "legacy", nothing is read or written.
 * With v2 enabled: `?design=v2|legacy` is an explicit choice (stored), else the device's display key.
 */
export function readInitialDesign(search: string, local: KeyValueStorage, session?: KeyValueStorage | null): Design {
  if (!isV2Enabled()) return "legacy";
  try {
    migrateUiPrefs(local);
    const fromUrl = new URLSearchParams(search).get("design");
    if (fromUrl === "v2" || fromUrl === "legacy") {
      recordUiChoice("design", fromUrl, local, session);
      return fromUrl;
    }
    return readDisplayPrefs(local).design;
  } catch {
    return "legacy";
  }
}
